/**
 * Меню долгого нажатия на строку списка чатов (аудит удобства 2026-10-04, п. 2, 9, 10, 13).
 *
 * <p>Раньше у строк разных типов были разные жесты: удержание звонило только в личке, свайп
 * выводил только из группы, а удаление висело молнией на каждой строке — и у подписчика канала
 * тоже, хотя удалить чужой канал он не может (сервер отвечал английской фразой). Теперь жест
 * один — удержание открывает меню, а что в нём есть, решает эта функция по типу чата и роли.
 *
 * <p>Правила здесь, а не в разметке, по той же причине, что и {@link ./chatTileActions}: «кому
 * что доступно» проверяется тестом быстрее и надёжнее, чем удержанием строки на устройстве.
 */

import type { ChatSummary } from '../../api/types';
import { chatTileCallPeerId, chatTileLeaveKind } from './chatTileActions';

export type ChatRowMenuItem =
  | 'mute'
  | 'unmute'
  | 'markRead'
  | 'callAudio'
  | 'callVideo'
  | 'deleteForMe'
  | 'leaveGroup'
  | 'unsubscribe'
  | 'deleteGroup'
  | 'deleteChannel';

type ChatRow = Pick<ChatSummary, 'type'> & Partial<Pick<ChatSummary, 'peerUserId' | 'myRole'>>;

/**
 * Пункты меню по порядку показа. Разрушающее — последним: палец, который промахнулся мимо
 * «Отключить звук», не должен попадать в «Удалить».
 *
 * <p>«Удалить у всех» для группы и канала — только администратору. Остальным вместо удаления
 * — «Покинуть» и «Отписаться»: это то, что они на самом деле могут сделать (п. 10).
 */
export function chatRowMenuItems(chat: ChatRow, state: { muted: boolean; unread: number }): ChatRowMenuItem[] {
  if (chat.type === 'SAVED') return [];
  const items: ChatRowMenuItem[] = [state.muted ? 'unmute' : 'mute'];
  if (state.unread > 0) items.push('markRead');
  if (chatTileCallPeerId(chat)) items.push('callAudio', 'callVideo');
  if (chat.type === 'DIRECT') {
    items.push('deleteForMe');
    return items;
  }
  const leave = chatTileLeaveKind(chat);
  if (leave === 'groupLeave') items.push('leaveGroup');
  else if (leave === 'channelUnsubscribe') items.push('unsubscribe');
  else if (chat.myRole === 'ADMIN') items.push(chat.type === 'CHANNEL' ? 'deleteChannel' : 'deleteGroup');
  return items;
}

export type MuteDuration = '1h' | '8h' | 'forever';

/** Срок заглушения; `null` — навсегда. */
export function muteUntil(duration: MuteDuration, now: number = Date.now()): Date | null {
  if (duration === '1h') return new Date(now + 60 * 60 * 1000);
  if (duration === '8h') return new Date(now + 8 * 60 * 60 * 1000);
  return null;
}

/**
 * Заглушён ли чат сейчас — по тому, что прислал сервер, с поправкой на срок: список не
 * перезагружается сам по истечении часа, и без проверки срока значок висел бы и после него.
 */
export function isChatMutedNow(chat: Pick<ChatSummary, 'muted' | 'mutedUntil'>, now: number = Date.now()): boolean {
  if (!chat.muted) return false;
  if (!chat.mutedUntil) return true;
  const until = Date.parse(chat.mutedUntil);
  return Number.isNaN(until) ? true : until > now;
}
